import { Fragment, useState } from 'react';
import { FileInputProps, FileInput, FileInputChildrenType } from 'shared/FileInput';
import { UploadBtn } from 'shared/UploadBtn';
import { useTranslator } from '@picsart/rc/services/localization';
import { nanoid } from 'nanoid/non-secure';
import { TempScenes, TempAudio } from 'types/Scene';
import { ChooseFilesForImportModal } from './ChooseFilesForImportModal';

const TICKS_PER_SECONDS = 254016000000;

type TempFiles = { [key: string]: string };
type UploadLogFileProps = {
  uploadLogFileHandler: (tempScenes: TempScenes, tempAudio: TempAudio) => void;
};

export const XmlUploader = ({ uploadLogFileHandler }: UploadLogFileProps) => {
  const [tempScenes, setTempScenes] = useState<TempScenes>(null);
  const [files, setFiles] = useState<TempFiles>(null);
  const [tempAudio, setTempAudio] = useState<TempAudio>(null);
  const translator = useTranslator();
  const handleUpload: FileInputProps['onChange'] = f => {
    const reader = new FileReader();
    reader.addEventListener('load', e => {
      parseXml(String(e.target.result));
    });
    reader.readAsText(f[0]);
  };

  const parseXml = (xml: string) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xml, 'text/xml');

    // video->clipitem->file->name
    const video = xmlDoc.getElementsByTagName('video')[0];
    const clipitemElements = video.getElementsByTagName('clipitem');
    const ts: TempScenes = {};
    const tf: TempFiles = {};

    Array.from(clipitemElements).forEach(clipitemElement => {
      const fileElement = clipitemElement.getElementsByTagName('file')[0];
      const fileId = fileElement.getAttribute('id');
      const nameElement = fileElement.getElementsByTagName('name')[0];

      if (!(fileId in tf)) {
        tf[fileId] = nameElement.textContent;
      }

      const pproTicksInElement = clipitemElement.getElementsByTagName('pproTicksIn')[0];
      const pproTicksIn = parseFloat(pproTicksInElement.textContent);
      const pproTicksOutElement = clipitemElement.getElementsByTagName('pproTicksOut')[0];
      const pproTicksOut = parseFloat(pproTicksOutElement.textContent);

      const id = nanoid();
      ts[id] = {
        id,
        trimStart: (pproTicksIn / TICKS_PER_SECONDS) * 1000,
        duration: ((pproTicksOut - pproTicksIn) / TICKS_PER_SECONDS) * 1000,
        fileId
      };
    });

    setTempScenes(ts);
    setFiles(tf);

    // audio->clipitem->file->name
    const audios = xmlDoc.getElementsByTagName('audio');
    const audio = Array.from(audios).find(item => item.getElementsByTagName('clipitem').length > 0);
    const audioClipitemElements: any = audio ? audio.getElementsByTagName('clipitem') : [];
    const audioClipitemElement = audioClipitemElements[0];
    if (audioClipitemElement) {
      const audioFileElements = audioClipitemElements[0].getElementsByTagName('file');
      const audioNameElement = audioFileElements[0]
        ? audioFileElements[0].getElementsByTagName('name')[0]
        : null;
      const pproTicksInElement = audioClipitemElement.getElementsByTagName('pproTicksIn')[0];
      const pproTicksIn = parseFloat(pproTicksInElement.textContent);
      const pproTicksOutElement = audioClipitemElement.getElementsByTagName('pproTicksOut')[0];
      const pproTicksOut = parseFloat(pproTicksOutElement.textContent);

      setTempAudio({
        name: audioNameElement?.textContent,
        trimStart: (pproTicksIn / TICKS_PER_SECONDS) * 1000,
        trimDuration: ((pproTicksOut - pproTicksIn) / TICKS_PER_SECONDS) * 1000
      });
    }
  };

  return (
    <Fragment>
      <FileInput onChange={handleUpload} accept="text/xml">
        {(props: FileInputChildrenType) => (
          <UploadBtn loading={false} {...props} sx={{ maxWidth: 140 }}>
            {translator('Upload_log')}
          </UploadBtn>
        )}
      </FileInput>
      <ChooseFilesForImportModal
        isOpen={!!files}
        closeHandler={() => {
          setTempScenes(null);
          setFiles(null);
          setTempAudio(null);
        }}
        tempScenes={tempScenes}
        fileNames={files}
        tempAudio={tempAudio}
        applyHandler={uploadLogFileHandler}
      />
    </Fragment>
  );
};
